import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import api from '../../utils/apiClient';
import APIConstant from '../../utils/pathConstants';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import Loader from '../Shared/Loader/Loader';
import { useCatchResponse } from '../../utils/useCatchResponse';
import { useTheme } from '../../context/ThemeContext';
import ThemeCard from './ThemeCard';
import AddEditThemeMaster from '../Masters/ThemeMaster/AddEditThemeMaster';

const ThemePicker = forwardRef((_props, ref) => {
  const { catchResponse } = useCatchResponse();
  const { reloadTheme, applyDefaultTheme, activeThemeId } = useTheme();
  const overlayRef = useRef(null);
  const [globalThemes, setGlobalThemes] = useState([]);
  const [customThemes, setCustomThemes] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [userId, setUserId] = useState('');

  useEffect(() => {
    const loginData = CommonConfig.loginData();
    setUserId(loginData?._id || '');
  }, []);

  const loadThemes = useCallback(async (uid) => {
    const id = uid || CommonConfig.loginData()?._id;
    try {
      const [globalRes, customRes] = await Promise.all([
        api.post(APIConstant.path.getGlobalThemeList, { UserID: id }),
        api.post(APIConstant.path.getCustomThemeList, { UserID: id }),
      ]);
      if (globalRes?.success) setGlobalThemes(globalRes.data || []);
      if (customRes?.success) setCustomThemes(customRes.data || []);
    } catch (err) {
      catchResponse(err);
    }
  }, [catchResponse]);

  useImperativeHandle(ref, () => ({
    toggle: async (event) => {
      await loadThemes();
      overlayRef.current?.toggle(event);
    },
    hide: () => overlayRef.current?.hide(),
  }));

  const selectTheme = async (theme) => {
    try {
      Loader.show();
      const res = await api.post(APIConstant.path.setActiveTheme, {
        UserID: userId,
        ThemeId: theme._id,
      });
      if (res?.success) {
        Toast.success({ message: 'Theme applied' });
        await reloadTheme();
      } else {
        Toast.error({ message: res?.message || 'Failed to apply theme' });
      }
      Loader.hide();
      overlayRef.current?.hide();
    } catch (err) {
      Loader.hide();
      catchResponse(err);
    }
  };

  const applyDefault = async () => {
    try {
      Loader.show();
      const res = await api.post(APIConstant.path.clearActiveTheme, {
        UserID: userId,
      });
      if (res?.success) {
        Toast.success({ message: 'Default theme applied' });
        try {
          localStorage.removeItem('edutattva_theme');
        } catch (_) {}
        applyDefaultTheme();
      } else {
        Toast.error({ message: res?.message || 'Failed to apply default' });
      }
      Loader.hide();
      overlayRef.current?.hide();
    } catch (err) {
      Loader.hide();
      catchResponse(err);
    }
  };

  const onAddClose = async () => {
    setAddOpen(false);
    await loadThemes();
  };

  return (
    <>
      <OverlayPanel
        ref={overlayRef}
        showCloseIcon={false}
        dismissable
        style={{ width: 360 }}
      >
        <div
          style={{
            maxHeight: 400,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              color: '#1c515e',
              borderBottom: '1px solid #eee',
              paddingBottom: 6,
            }}
          >
            Select Theme
          </div>

          {globalThemes.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#666',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Global Themes
              </div>
              {globalThemes.map((t) => (
                <ThemeCard
                  key={t._id}
                  theme={t}
                  selected={t._id === activeThemeId}
                  onSelect={selectTheme}
                />
              ))}
            </div>
          )}

          {customThemes.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#666',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                My Themes
              </div>
              {customThemes.map((t) => (
                <ThemeCard
                  key={t._id}
                  theme={t}
                  selected={t._id === activeThemeId}
                  onSelect={selectTheme}
                />
              ))}
            </div>
          )}

          <div
            onClick={() => setAddOpen(true)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 14,
              cursor: 'pointer',
              border: '1px dashed #bbb',
              borderRadius: 6,
              color: '#666',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <i className="pi pi-plus" style={{ fontSize: 18 }} />
            <span style={{ fontSize: 12, marginTop: 4 }}>Add Custom Theme</span>
          </div>

          {activeThemeId && (
            <Button
              label="Apply Default Theme"
              severity="danger"
              outlined
              onClick={applyDefault}
            />
          )}
        </div>
      </OverlayPanel>

      <Dialog
        header="Add Theme Master"
        visible={addOpen}
        onHide={onAddClose}
        draggable={false}
        position="center"
        style={{ width: '650px' }}
      >
        <AddEditThemeMaster
          masterData={{}}
          isEdit={false}
          ID=""
          onCancel={onAddClose}
        />
      </Dialog>
    </>
  );
});

export default ThemePicker;
